<template>
    <tr
        ref="wrapper"
        class="snapgrid-row"
        :class="{
            [theme.wrapper]: true,
            [theme.standard]: !isPlaceholder,
            [theme.placeholder]: isPlaceholder,
        }"
        :style="row.style"
        @mouseover="mouseover = true"
        @mouseout="mouseover = false"
        @click.stop="click"
        @contextmenu.prevent="showContextMenu"
    >
        <td
            v-if="config.checkboxes_enabled"
            class="snapgrid-cell"
            :class="{
                [rowClass]: true,
                [theme.outlined]: showOutlinedTheme,
                [theme.firstRow]: showFirstRowTheme,
                [theme.lastRow]: showLastRowTheme,
                [theme.cell]: true,
                [theme.checkbox]: true,
            }"
        >
            <input
                v-model="row.checked"
                type="checkbox"
                @change="check"
                @click.stop
            />
        </td>

        <td
            v-for="(cell, cIndex) in cells"
            :key="`cell-${cIndex}`"
            class="snapgrid-cell"
            :colspan="colspan"
            :dusk="
                !isPlaceholder && row.index === 0 && cIndex === 0
                    ? 'snapgrid-first-row'
                    : ''
            "
            :class="{
                [rowClass]: true,
                [theme.outlined]: showOutlinedTheme,
                [theme.firstRow]: showFirstRowTheme,
                [theme.lastRow]: showLastRowTheme,
                [theme.cell]: true,
                [theme.nowrap]: !data.cols[cIndex].wrap_cell_text,
            }"
            :style="`${cellStyles[cIndex]}; ${row.style}`"
            :title="titles[cIndex]"
        >
            <span v-if="isPlaceholder">&nbsp;</span>
            <!-- eslint-disable vue/no-v-html -->
            <span v-else v-html="cell"></span>
        </td>
    </tr>
</template>

<script>
import SnapGridRowInterface from './../classes/SnapGridRowInterface';
import Common from './mixins/Common.vue';
import UsesHandlers from './mixins/UsesHandlers.vue';
import UsesBindRelations from './mixins/UsesBindRelations.vue';
import { loadPanel } from 'larasnap';
import { numberToCurrency } from 'larasnap/support';
import { DataType } from 'larasnap/enums';

export default {
    mixins: [Common, UsesHandlers, UsesBindRelations],
    props: {
        data: Object,
        row: Object,
        index: Number,
        isPlaceholder: Boolean,
        cellStyles: Array,
    },
    data() {
        return {
            binding: null,
            mouseover: false,
            titles: [],
        };
    },
    computed: {
        showOutlinedTheme() {
            return (
                !this.isPlaceholder &&
                this.row.checked &&
                this.data.contextMenuVisible
            );
        },
        showFirstRowTheme() {
            return !this.isPlaceholder && this.isFirstCheckedRow;
        },
        showLastRowTheme() {
            return !this.isPlaceholder && this.isLastCheckedRow;
        },
        isFirstCheckedRow() {
            return !this.previousRow || !this.previousRow.checked;
        },
        isLastCheckedRow() {
            return !this.nextRow || !this.nextRow.checked;
        },
        previousRow() {
            if (this.row.index > 0) {
                return this.rows[this.row.index - 1];
            } else {
                return null;
            }
        },
        nextRow() {
            if (this.row.index < this.rows.length - 1) {
                return this.rows[this.row.index + 1];
            } else {
                return null;
            }
        },
        colspan() {
            if (this.isPlaceholder) {
                return this.data.cols.length;
            } else {
                return 1;
            }
        },
        cells() {
            if (this.isPlaceholder) {
                return [''];
            } else {
                return this.formatCells(this.row.cells);
            }
        },
        rowClass() {
            if (this.isPlaceholder) {
                return '';
            } else if (this.row.checked) {
                return this.theme.checked;
            } else if (this.isBoundToPanel) {
                return this.theme.selected;
            } else if (this.mouseover) {
                return this.theme.hover;
            } else {
                return '';
            }
        },
        isBoundToPanel() {
            return !!this.props.binds.find(
                bind => bind.type === 'Panel' && bind.data == this.row.id,
            );
        },
    },
    watch: {
        index: function (index) {
            if (!this.isPlaceholder) {
                this.row.index = index;
            }
        },
    },
    created() {
        if (!this.isPlaceholder) {
            this.row.index = this.index;
        }

        if (!this.isPlaceholder) {
            this.bus.$on('context-menu-hidden', () => {
                if (!this.config.checkboxes_enabled) {
                    this.row.checked = false;
                }
            });
        }
    },
    mounted() {
        if (!this.isPlaceholder) {
            setTimeout(() => {
                $(this.$refs.wrapper)
                    .find('a')
                    .click(event => {
                        event.stopPropagation();
                    });
            }, 350); // Not sure why this has to be so high

            this.bus.$on('context-menu-show', () => {
                if (!this.config.checkboxes_enabled) {
                    this.row.checked = false;
                }
            });
        }

        this.generateCellHoverText();
    },
    methods: {
        generateCellHoverText() {
            this.cells.forEach(cell => {
                const div = document.createElement('div');
                div.innerHTML = cell;
                const text = div.textContent || div.innerText || '';
                this.titles.push(text);
            });
        },
        showContextMenu() {
            if (this.config.context_menu_enabled) {
                const row = new SnapGridRowInterface(this.row);
                this.bus.$emit('context-menu-show');
                this.runHandler('context-menu-show', row);
                const abort =
                    this.data.interface.contextMenuShow(row) === false;

                this.row.checked = true;

                if (abort) {
                    this.bus.$emit('context-menu-hide');
                }
            }
        },
        formatCells(cells) {
            const cols = this.data.cols;

            for (let i = 0, ii = cells.length; i < ii; i++) {
                const col = cols[i];
                const cell = cells[i];
                const datatype = col.datatype;

                /*if (datatype === DataType.DATE && cell) {
                        cells[i] = moment(cell).format(col.date_format);
                    } else if ((datatype === DataType.DATETIME || datatype === DataType.TIMESTAMP) && cell) {
                        cells[i] = moment(cell).format(col.datetime_format);  
                    } else*/
                if (datatype === DataType.CURRENCY) {
                    cells[i] = numberToCurrency(cell, {
                        decimals: col.decimals ?? 2,
                    });
                } else if (datatype === DataType.YES_NO) {
                    cells[i] = cell ? 'Yes' : 'No';
                } else if (datatype === DataType.PERCENTAGE) {
                    cells[i] =
                        Math.round(cell * 1000) / 10 +
                        `<span class="${this.theme.percentage}">%</span>`;
                } else if (
                    col.show_commas &&
                    datatype === DataType.INTEGER &&
                    cells[i]
                ) {
                    cells[i] = String(cells[i]).replace(
                        /\B(?=(\d{3})+(?!\d))/g,
                        ',',
                    );
                }
            }

            return cells;
        },
        check() {
            if (this.config.allow_multiple_checked_rows) {
                if (this.data.shiftKey) {
                    const lastClickedRow = this.rows.find(x => x.lastClicked);
                    if (lastClickedRow) {
                        this.checkRange(lastClickedRow.index, this.row.index);
                    }
                }
            } else {
                this.rows
                    .filter(x => x.id !== this.row.id)
                    .forEach(x => (x.checked = false));
            }

            this.runHandler('row-check', this.row);

            this.data.rows.forEach(x => (x.lastClicked = false));
            this.row.lastClicked = true;
            this.bus.$emit('context-menu-hide');
        },
        checkRange(startIndex, endIndex) {
            const checked = this.row.checked;

            if (startIndex < endIndex) {
                for (let i = startIndex; i <= endIndex; i++) {
                    this.rows[i].checked = checked;
                }
            } else {
                for (let i = startIndex; i >= endIndex; i--) {
                    this.rows[i].checked = checked;
                }
            }
        },
        click(event) {
            if (event?.ctrlKey) {
                console.log(this.row.id);
                return;
            }

            if (this.config.checkboxes_enabled && this.config.check_on_click) {
                this.row.checked = !this.row.checked;
                this.check();
            }

            this.loadPanel();
        },
        loadPanel() {
            const name = this.getBindRelation('Panel');
            if (name) {
                loadPanel({
                    name: name,
                    params: [this.row.id],
                    row: new SnapGridRowInterface(this.row),
                    parent: $(this.$refs.wrapper).panel(),
                });
            }
        },
    },
};
</script>

<style lang="scss" module></style>
<style
    lang="scss"
    module="theme_default"
    src="./../css/themes/default/row.scss"
></style>
<style
    lang="scss"
    module="theme_simple"
    src="./../css/themes/simple/row.scss"
></style>
